import { z } from "zod";

/** 個人メモ(SPEC §13.5)。共有は Google ドライブのように、個別(メール)と社内の全員に、閲覧者・編集者で行う。 */
export const MEMO_TITLE_MAX = 200;
export const MEMO_BODY_MAX = 100_000;
export const MEMO_SHARES_MAX = 100;

export const memoRoleSchema = z.enum(["VIEWER", "EDITOR"]);
export type MemoRoleValue = z.infer<typeof memoRoleSchema>;
/** 自分のメモなら OWNER。共有されたメモなら共有された権限。 */
export const memoAccessRoleSchema = z.enum(["OWNER", "EDITOR", "VIEWER"]);
export type MemoAccessRoleValue = z.infer<typeof memoAccessRoleSchema>;

export const memoReadStateSchema = z.enum(["READ", "UNREAD", "UPDATED"]);
export type MemoReadStateValue = z.infer<typeof memoReadStateSchema>;

export const memoShareSchema = z.object({
  email: z.email().max(254),
  role: memoRoleSchema,
});
export type MemoShareValue = z.infer<typeof memoShareSchema>;

/** 一覧の 1 件。本文は含めず、抜粋だけ返す。 */
export const memoSummarySchema = z.object({
  id: z.string(),
  title: z.string(),
  /** アイコン(絵文字、またはサービスのアイコンの名前)。未設定は空。 */
  icon: z.string().default(""),
  excerpt: z.string(),
  ownerEmail: z.email(),
  /** 一覧の「作成が新しい順」に使う。 */
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  /** 自分の権限。OWNER 以外は共有されたメモ。 */
  role: memoAccessRoleSchema,
  /** 誰かに(または社内全員に)共有しているか。所有者の一覧で「共有中」を出すため。 */
  shared: z.boolean(),
  /** アーカイブしたか(所有者の「アーカイブ」の表示にだけ出る)。 */
  archived: z.boolean().default(false),
  /** 属するプロジェクト(プロジェクトのメモ。§13.6)。ホームのメモは null。 */
  projectId: z.string().nullable().default(null),
  /** ボードにピンで留めた日時(プロジェクトのメモだけ)。留めていなければ null。 */
  pinnedAt: z.iso.datetime().nullable().default(null),
  /** ピンのメモの、自分にとっての状態(未読・開いたあと更新・既読)。ピンでなければ null。 */
  readState: memoReadStateSchema.nullable().default(null),
  revision: z.number().int().positive(),
});
export type MemoSummary = z.infer<typeof memoSummarySchema>;

export const memoViewSchema = memoSummarySchema.extend({
  body: z.string(),
  updatedBy: z.email(),
  /** 共有の中身は、所有者にだけ返す(共有された側には空と null)。 */
  shares: z.array(memoShareSchema),
  orgAccess: memoRoleSchema.nullable(),
  canEdit: z.boolean(),
  canManage: z.boolean(),
  /** ボードにピンで留められるか(プロジェクトのメモで、参加者か作った人)。 */
  canPin: z.boolean().default(false),
});
export type MemoView = z.infer<typeof memoViewSchema>;

/** archived: 自分がアーカイブしたメモ。ほかの表示にはアーカイブしたメモを出さない。 */
export const memoScopeSchema = z.enum(["all", "mine", "shared", "archived"]);
export type MemoScope = z.infer<typeof memoScopeSchema>;

export const memoListQuerySchema = z.object({
  /** 空白区切りの全語がタイトルか本文に含まれるメモ。 */
  q: z.string().max(200).optional(),
  scope: memoScopeSchema.default("all"),
});

export const memoListResponseSchema = z.object({
  memos: z.array(memoSummarySchema),
  /** 上限(100 件)で打ち切ったか。 */
  truncated: z.boolean(),
});
export type MemoListResponse = z.infer<typeof memoListResponseSchema>;

export const memoResponseSchema = z.object({ memo: memoViewSchema });
export type MemoResponse = z.infer<typeof memoResponseSchema>;

export const createMemoRequestSchema = z.object({
  title: z.string().max(MEMO_TITLE_MAX).default(""),
  body: z.string().max(MEMO_BODY_MAX).default(""),
  icon: z.string().max(64).default(""),
  /** プロジェクトのメモとして作るなら、そのプロジェクトの ID(参加者だけ)。 */
  projectId: z.string().min(1).max(100).nullable().optional(),
});
export type CreateMemoRequest = z.input<typeof createMemoRequestSchema>;

export const updateMemoRequestSchema = z.object({
  title: z.string().max(MEMO_TITLE_MAX).optional(),
  body: z.string().max(MEMO_BODY_MAX).optional(),
  icon: z.string().max(64).optional(),
  revision: z.number().int().positive(),
});
export type UpdateMemoRequest = z.infer<typeof updateMemoRequestSchema>;

export const updateMemoSharingRequestSchema = z.object({
  shares: z.array(memoShareSchema).max(MEMO_SHARES_MAX),
  orgAccess: memoRoleSchema.nullable(),
  revision: z.number().int().positive(),
});
export type UpdateMemoSharingRequest = z.infer<typeof updateMemoSharingRequestSchema>;

/** アーカイブする・元に戻す(所有者だけ)。 */
export const updateMemoArchivedRequestSchema = z.object({
  archived: z.boolean(),
  revision: z.number().int().positive(),
});
export type UpdateMemoArchivedRequest = z.infer<typeof updateMemoArchivedRequestSchema>;

/** ボードにピンで留める・外す(プロジェクトのメモだけ。参加者と作った人)。 */
export const updateMemoPinnedRequestSchema = z.object({
  pinned: z.boolean(),
  revision: z.number().int().positive(),
});
export type UpdateMemoPinnedRequest = z.infer<typeof updateMemoPinnedRequestSchema>;

/** プロジェクトのメモの一覧(§13.6)。アーカイブしたものは archived の表示にだけ出す。 */
export const projectMemoScopeSchema = z.enum(["all", "archived"]);
export type ProjectMemoScope = z.infer<typeof projectMemoScopeSchema>;

export const projectMemoListQuerySchema = z.object({
  q: z.string().max(200).optional(),
  scope: projectMemoScopeSchema.default("all"),
});

export const projectMemoListResponseSchema = memoListResponseSchema.extend({
  /** 自分がそのプロジェクトの参加者で、メモを作れる・ピンで留められるか。 */
  canCreate: z.boolean(),
});
export type ProjectMemoListResponse = z.infer<typeof projectMemoListResponseSchema>;

/** ボードのピンのうち、自分にとって未読・更新のものの件数(プロジェクトごと。0 件のプロジェクトは含めない)。 */
export const memoUnreadPinsResponseSchema = z.object({
  projects: z.array(z.object({ projectId: z.string(), count: z.number().int().positive() })),
});
export type MemoUnreadPinsResponse = z.infer<typeof memoUnreadPinsResponseSchema>;
